// Source: ai-cost-audit/ (repo root), captured 2026-08-14, window 2026-07-16..2026-08-14 (30d),
// plus a follow-up codebase sweep on 2026-08-19 that walked every repo in the
// workspace (not just backend) for non-AI paid vendors — comms, CRM, payments,
// analytics, e-signature, LOS integrations, etc. — that the first pass missed.
// This is a manually-curated snapshot, not a live feed — most providers here have
// no billing API access yet (see MISSING_INTEGRATIONS). Refresh by re-running the
// audit and updating this file until real billing connectors exist.

export const AUDIT_WINDOW = "2026-07-16 .. 2026-08-14 (30d)";
export const CAPTURED_AT = "2026-08-19";

export const SUMMARY = {
  measurable30d: 12798.42,
  last7d: 3363.62,
  todayPartial: 233.55,
  avgDaily: 426.61,
  largestProvider: "Google Cloud (Vertex AI + Document AI)",
  largestProviderCost: 8688.94,
  largestModel: "gemini-3-flash-preview (Vertex, on-demand)",
  providersFoundInCode: 54,
  providersWithConfirmedBilling: 1,
  distinctModelsReferenced: "48+",
  modelsWithConfirmedUsage: 9,
};

// Confidence: CONFIRMED (real billing/usage API) > RECONSTRUCTED (usage x list price)
// > ESTIMATED (partial/derived) > UNKNOWN (no data at all).
export const PROVIDERS = [
  {
    provider: "Google Cloud",
    service: "Vertex AI (Gemini) — prod",
    category: "AI / LLM",
    cost30d: 8688.94,
    pctOfMeasured: 67.9,
    billingMethod: "GCP invoice (on-demand portion)",
    activeModels: ["gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemini-2.5-flash"],
    primaryUse: "Doc extraction, classification, Ask AI",
    production: true,
    confidence: "RECONSTRUCTED",
    notes: "On-demand (spillover + shared) only. Dedicated/PT tokens billed separately as a fixed GSU commitment of unknown value.",
  },
  {
    provider: "Google Cloud",
    service: "Vertex AI Provisioned Throughput — prod",
    category: "AI / LLM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP committed use (GSU)",
    activeModels: ["gemini-3-flash-preview"],
    primaryUse: "Same as above, dedicated capacity",
    production: true,
    confidence: "UNKNOWN",
    notes: "15.75B input + 2.61B output tokens/30d on dedicated capacity. At on-demand rates that volume would run ~$15,710/mo — the commitment is presumably below that, but the actual charge is invisible without billing access.",
  },
  {
    provider: "Google Cloud",
    service: "Vertex AI (Gemini) — dev",
    category: "AI / LLM",
    cost30d: 1305.95,
    pctOfMeasured: 10.2,
    billingMethod: "GCP invoice",
    activeModels: ["gemini-3-flash-preview", "gemini-2.5-flash", "gemini-2.5-pro"],
    primaryUse: "Development / testing",
    production: false,
    confidence: "RECONSTRUCTED",
    notes: "1.65B tokens/30d on a non-production project — production-scale volume with no budget cap.",
  },
  {
    provider: "Google Cloud",
    service: "Document AI — prod",
    category: "AI / OCR",
    cost30d: 1614.57,
    pctOfMeasured: 12.6,
    billingMethod: "GCP invoice",
    activeModels: ["pretrained-ocr-v2.0", "pretrained-form-parser-v2.0", "pretrained-bankstatement-v3.0"],
    primaryUse: "Document OCR + form extraction",
    production: true,
    confidence: "RECONSTRUCTED",
    notes: "102,955 pages/30d. Form Parser runs $30/1k pages vs OCR $1.50/1k on nearly identical page volume.",
  },
  {
    provider: "Google Cloud",
    service: "Document AI — dev",
    category: "AI / OCR",
    cost30d: 8.29,
    pctOfMeasured: 0.1,
    billingMethod: "GCP invoice",
    activeModels: ["pretrained-form-parser-v2.1", "pretrained-ocr-v2.1"],
    primaryUse: "Development",
    production: false,
    confidence: "RECONSTRUCTED",
    notes: "512 pages/30d.",
  },
  {
    provider: "Google Cloud",
    service: "Gemini API (generativelanguage) — prod",
    category: "AI / Voice",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP invoice",
    activeModels: ["gemini-2.5-flash", "gemini-3.1-flash-tts", "gemini-3-flash-live", "gemini-2.5-flash-native-audio-dialog"],
    primaryUse: "Voice agent, TTS, live dictation",
    production: true,
    confidence: "ESTIMATED",
    notes: "213,655 requests/30d. Only output tokens exposed (912,767) — input tokens are not measurable, so cost cannot be fully computed.",
  },
  {
    provider: "OpenAI",
    service: "Chat Completions + Embeddings",
    category: "AI / LLM",
    cost30d: 1059.55,
    pctOfMeasured: 8.3,
    billingMethod: "Direct invoice (OpenAI Organization Costs API)",
    activeModels: ["gpt-5.4", "gpt-4o-mini", "gpt-5.4-mini", "gpt-5.6-luna", "gpt-5.6-sol", "gpt-4o", "gpt-4.1-mini", "text-embedding-3-large/small"],
    primaryUse: "UW conditions, classification, chat, embeddings",
    production: true,
    confidence: "CONFIRMED",
    notes: "The only provider with confirmed billing in this audit. gpt-5.4 input tokens alone are 63% of this line. Prev month $292.13 -> projected this month $2,040.33 (+215%).",
  },
  {
    provider: "Anthropic",
    service: "Messages API",
    category: "AI / LLM",
    cost30d: 121.12,
    pctOfMeasured: 0.9,
    billingMethod: "Direct invoice",
    activeModels: ["claude-opus-4-7", "claude-sonnet-4-5", "claude-haiku-4-5"],
    primaryUse: "Doc classification, portal guardrails, Ask Addy export",
    production: true,
    confidence: "ESTIMATED",
    notes: "Figure is a Langfuse-traced floor only, not an invoice total — true spend is likely higher.",
  },
  {
    provider: "Microsoft Azure",
    service: "Azure OpenAI",
    category: "AI / LLM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Azure invoice",
    activeModels: ["gpt-4o", "gpt-4o-mini", "gpt-4.1", "gpt-5.6-luna", "text-embedding-3-large"],
    primaryUse: "Fallback LLM + embeddings (gmail-server, backend)",
    production: true,
    confidence: "UNKNOWN",
    notes: "Two endpoints in active use (addy-ai-azure, addy-ai-us-east-2). Entirely unmeasured — complete blind spot.",
  },
  {
    provider: "Vapi",
    service: "Voice AI agent platform",
    category: "AI / Voice",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: ["gpt-4o (pinned)"],
    primaryUse: "Outbound/inbound voice agent",
    production: true,
    confidence: "UNKNOWN",
    notes: "Bundles telephony + STT + LLM + TTS. Possible double-count with the OpenAI gpt-4o line if Vapi bills through our OpenAI org.",
  },
  {
    provider: "Modal",
    service: "Serverless GPU (A100/L4) OCR",
    category: "Infra / GPU",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: ["custom OCR"],
    primaryUse: "Document OCR (warm pool)",
    production: true,
    confidence: "UNKNOWN",
    notes: "Warm pool bills for idle capacity — a classic silent cost.",
  },
  {
    provider: "Addy (self-hosted)",
    service: "In-house OCR on A100 VM",
    category: "Infra / GPU",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP invoice (Compute)",
    activeModels: ["custom OCR"],
    primaryUse: "Document OCR",
    production: true,
    confidence: "UNKNOWN",
    notes: "GPU VM cost that shows up under Compute Engine, not any AI SKU — invisible to AI-specific dashboards.",
  },
  {
    provider: "Groq",
    service: "LPU inference",
    category: "AI / LLM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: ["openai/gpt-oss-120b", "openai/gpt-oss-20b"],
    primaryUse: "LangChain LLM option",
    production: false,
    confidence: "UNKNOWN",
    notes: "GROQ_API_KEY configured, ChatGroq initialized at config.js:1547. No usage evidence found — likely dormant.",
  },
  {
    provider: "NVIDIA",
    service: "API Catalog + self-hosted vLLM",
    category: "AI / LLM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct / GPU compute",
    activeModels: ["z-ai/glm-5.2", "meta/llama-3.3-70b-instruct", "deepseek-ai/deepseek-r1", "qwen/qwen2.5-coder-32b", "qwen2.5-7b (self-hosted L4)"],
    primaryUse: "Inference service",
    production: false,
    confidence: "UNKNOWN",
    notes: "NvidiaInference.js. Self-hosted portion is GPU infra cost, not API cost.",
  },
  {
    provider: "Cursor",
    service: "Composer agents as LLM backend",
    category: "AI / LLM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription/API",
    activeModels: ["cursor-composer-2.5-fast", "composer-2.5"],
    primaryUse: "Inference backend + browser agent tasks",
    production: false,
    confidence: "UNKNOWN",
    notes: "Unusual: a coding-agent API is used as a general-purpose LLM backend (CursorInference.js).",
  },
  {
    provider: "Extend AI",
    service: "Document parsing",
    category: "AI / OCR",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "Document parse/split",
    production: true,
    confidence: "UNKNOWN",
    notes: "ExtendAI.js -> api.extend.ai. Overlaps with Document AI and in-house OCR.",
  },
  {
    provider: "Qdrant",
    service: "Vector database",
    category: "Infra / Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct / cloud",
    activeModels: [],
    primaryUse: "Embeddings vector store",
    production: true,
    confidence: "UNKNOWN",
    notes: "Used by backend + gmail-server. 3072-dim vectors inflate storage cost.",
  },
  {
    provider: "Langfuse",
    service: "LLM observability",
    category: "Infra / Observability",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Tracing / cost analytics",
    production: true,
    confidence: "UNKNOWN",
    notes: "Traced $9,864.97/30d of AI spend but its own subscription cost is unknown. Also understates Gemini cost due to a model-alias pricing bug.",
  },
  {
    provider: "LlamaIndex",
    service: "LlamaParse",
    category: "AI / OCR",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "Document parsing",
    production: false,
    confidence: "UNKNOWN",
    notes: "LlamaParseReader imported in embeddingsVectorDBUtilsQdrant.js:60.",
  },
  {
    provider: "Cohere",
    service: "Rerank",
    category: "AI / LLM",
    cost30d: 0,
    pctOfMeasured: 0,
    billingMethod: "Direct",
    activeModels: ["rerank-english-v3.0"],
    primaryUse: "Reranking",
    production: false,
    confidence: "CONFIRMED-inactive",
    notes: "CohereRerank instantiation is commented out at embeddingsVectorDBUtilsQdrant.js:1814-1816.",
  },
  {
    provider: "Apify",
    service: "Web scraping",
    category: "Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "People data enrichment",
    production: true,
    confidence: "UNKNOWN",
    notes: "peopleDataLabs.js",
  },
  {
    provider: "Scale SERP",
    service: "Search API",
    category: "Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "Web search",
    production: true,
    confidence: "UNKNOWN",
    notes: "Multiple rotating keys in scaleSERP.js:4-11.",
  },
  {
    provider: "Tavily",
    service: "Search API for LLMs",
    category: "Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "Agent web search",
    production: false,
    confidence: "UNKNOWN",
    notes: "TavilySearch in OpenAINew.js:22.",
  },
  {
    provider: "Algolia",
    service: "Search index",
    category: "Infra / Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct",
    activeModels: [],
    primaryUse: "Help center search",
    production: true,
    confidence: "UNKNOWN",
    notes: "search/Algolia.js",
  },
  {
    provider: "HuggingFace",
    service: "Model hub (SPLADE)",
    category: "AI / LLM",
    cost30d: 0,
    pctOfMeasured: 0,
    billingMethod: "Free / self-hosted",
    activeModels: ["naver/splade-v3"],
    primaryUse: "Sparse embeddings (gmail-server)",
    production: true,
    confidence: "CONFIRMED",
    notes: "Runs locally, no API cost — compute cost only.",
  },
  {
    provider: "Tesseract (OSS)",
    service: "Local OCR",
    category: "AI / OCR",
    cost30d: 0,
    pctOfMeasured: 0,
    billingMethod: "None",
    activeModels: ["eng.traineddata"],
    primaryUse: "Client + server OCR",
    production: true,
    confidence: "CONFIRMED",
    notes: "No API cost, CPU only.",
  },

  // --- Found in the 2026-08-19 non-AI sweep (backend/, client/, gmail-server/, etc.) ---
  // None of these have billing access yet; they were invisible to the first audit
  // because it searched for AI SDKs specifically, not comms/CRM/payments vendors.
  {
    provider: "Twilio",
    service: "SMS + voice",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (usage-based)",
    activeModels: [],
    primaryUse: "Transactional SMS and voice calls",
    production: true,
    confidence: "UNKNOWN",
    notes: "Extensive usage: backend/functions/services/twilio.js, controllers/twilio.js, routes/twilio.js, models/Twilio.js.",
  },
  {
    provider: "Telnyx",
    service: "SMS + voice (backup)",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (usage-based)",
    activeModels: [],
    primaryUse: "Backup/failover SMS and voice to Twilio",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/functions/services/telnyx.js:5 — TELNYX_API_BASE_URL.",
  },
  {
    provider: "SendGrid",
    service: "Transactional email",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (usage-based)",
    activeModels: [],
    primaryUse: "Transactional email sending",
    production: true,
    confidence: "UNKNOWN",
    notes: "@sendgrid/mail used in services/CategorySummaryEmailDispatcher.js, services/nodeMailer.js.",
  },
  {
    provider: "Brevo (Sendinblue)",
    service: "Marketing/list email",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (subscription + usage)",
    activeModels: [],
    primaryUse: "Marketing/list email campaigns",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/functions/services/brevo.js — SibApiV3Sdk, BrevoService class.",
  },
  {
    provider: "Zoom",
    service: "Server-to-Server OAuth API",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Admin whitelist + automated demo-host meeting scheduling",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/zoomWhitelistService.js and services/zoomMeetingAdminClient.js (admin scopes, cron-driven).",
  },
  {
    provider: "Slack",
    service: "Slack API (beyond webhooks)",
    category: "Comms",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Incident alerts, live-chat thread sync, on-call routing",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/SlackService.js, incidentAlert/incidentAlertSlackClient.js, liveChat/syncSlackThreadReplies.js, liveChat/onCallRouting.js.",
  },
  {
    provider: "HubSpot",
    service: "CRM sync",
    category: "CRM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (seats + API tier)",
    activeModels: [],
    primaryUse: "Deals, contacts, cancellation pipeline sync",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/HubSpotSyncService.js plus 15+ callers across the backend.",
  },
  {
    provider: "Salesforce",
    service: "CRM integration",
    category: "CRM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (seats + API tier)",
    activeModels: [],
    primaryUse: "CRM integration",
    production: true,
    confidence: "UNKNOWN",
    notes: "jsforce in cloudFunctions/salesforce.js, controllers/salesforce.js, routes/salesforce.js.",
  },
  {
    provider: "GoHighLevel",
    service: "Marketing/CRM platform",
    category: "CRM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Lead import",
    production: true,
    confidence: "UNKNOWN",
    notes: "cloudFunctions/gohighlevel.js, services/GoHighLevel.js — pagination/rate-limit handling implies real prod traffic.",
  },
  {
    provider: "RadCrm",
    service: "Partner CRM",
    category: "CRM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Partner CRM with its own monthly billing job",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/RadCrm.js, services/radCrmPartnerBilling.js, triggers/runMonthlyRadCrmPartnerBilling.js.",
  },
  {
    provider: "PromoteKit",
    service: "Referral/affiliate tracking",
    category: "CRM",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Referral/affiliate tracking",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/PromoteKitService.js (backend) + client/types/promotekit.d.ts, PlanCard.tsx (client).",
  },
  {
    provider: "DocuSign",
    service: "E-signature",
    category: "Compliance",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (per-envelope)",
    activeModels: [],
    primaryUse: "NDA workflow",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/integrations/docusign/DocuSignConnectHandler.js — HMAC-verified Connect webhook mirroring status into HubSpot.",
  },
  {
    provider: "CheckReality (ScamAI)",
    service: "Document forensics / fraud check",
    category: "Compliance",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (per-check)",
    activeModels: [],
    primaryUse: "Document fraud/forensics checks",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/CheckReality.js:1-19 — sandbox + prod keys (CHECKREALITY_API_KEY, CHECKREALITY_VERIFI_INSURER_ID).",
  },
  {
    provider: "MeridianLink",
    service: "LOS integration",
    category: "LOS",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (partner integration)",
    activeModels: [],
    primaryUse: "Loan sync, underwriting conditions, closing worksheet",
    production: true,
    confidence: "UNKNOWN",
    notes: "cloudFunctions/meridianlinkSync.js, services/integrations/meridianlink/* (large surface). Also drives the gpt-5.4 UW-conditions AI cost — see O1 in opportunities.",
  },
  {
    provider: "LendingPad",
    service: "LOS integration",
    category: "LOS",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (partner integration)",
    activeModels: [],
    primaryUse: "Second loan-origination-system integration",
    production: true,
    confidence: "UNKNOWN",
    notes: "triggers/lendingPadActivationPoller.js, onOrganizationLendingPadApproved.js.",
  },
  {
    provider: "People Data Labs",
    service: "Contact/email enrichment",
    category: "Data",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (per-record)",
    activeModels: [],
    primaryUse: "Contact enrichment — distinct vendor from Apify, though it wraps ApifyClient internally as one data source",
    production: true,
    confidence: "UNKNOWN",
    notes: "config/config.js:283 PEOPLE_DATA_LABS_ENDPOINT; services/peopleDataLabs.js (backend); sidebar-chat/src/services/peopleDataLabs.js (client wrapper).",
  },
  {
    provider: "Fireflies.ai",
    service: "Meeting transcript ingestion",
    category: "Productivity",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Meeting-transcript ingestion via GraphQL, feeds HubSpot + lead-warmth scoring",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/FirefliesService.js:4 FIREFLIES_GRAPHQL_URL; consumed by HubSpotSyncService.js, LeadWarmthService.js.",
  },
  {
    provider: "Notion",
    service: "Notion API",
    category: "Productivity",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Notion briefings integration",
    production: true,
    confidence: "UNKNOWN",
    notes: "@notionhq/client, services/NotionService.js, NOTION_API_TOKEN in config. Powers the NotionBriefings admin page.",
  },
  {
    provider: "Calendly",
    service: "Scheduling automation",
    category: "Productivity",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription",
    activeModels: [],
    primaryUse: "Demo scheduling automation",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/CalendlyService.js, triggers/calendlyDevMeetingScheduler.js.",
  },
  {
    provider: "Atlassian (Jira/Confluence)",
    service: "Jira/Confluence API",
    category: "Productivity",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (existing org seats)",
    activeModels: [],
    primaryUse: "Engineering reporting, support ticket webhook, dedicated ticket-agent enrichment service",
    production: true,
    confidence: "UNKNOWN",
    notes: "ticket-agent repo (dedicated service) + backend triggers/dailyJiraEngineeringReport.js, triggers/jiraSupportWebhook.js. Likely an existing seat cost, not new incremental spend.",
  },
  {
    provider: "GitHub",
    service: "GitHub API (Octokit)",
    category: "Productivity",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (existing org)",
    activeModels: [],
    primaryUse: "Deployment tracking",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/octokit.js, models/Deployment.js. Standard API usage, unlikely to add cost unless Enterprise features are used.",
  },
  {
    provider: "Mixpanel",
    service: "Product analytics",
    category: "Analytics",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (event volume tiers)",
    activeModels: [],
    primaryUse: "Product analytics, backend and client",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/functions/utils/Mixpanel.js; client/components/mixpanelInit/MixpanelInit.tsx.",
  },
  {
    provider: "PostHog",
    service: "Product analytics + session replay",
    category: "Analytics",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (event volume tiers)",
    activeModels: [],
    primaryUse: "Product analytics + session replay, backend and client — separate spend from Langfuse's LLM tracing",
    production: true,
    confidence: "UNKNOWN",
    notes: "services/posthogReplayService.js, utils/PostHog.js (backend); components/posthog/PostHogProvider.tsx, utils/PostHog.ts (client).",
  },
  {
    provider: "Sentry",
    service: "Error tracking + session replay",
    category: "Analytics",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct subscription (event volume tiers)",
    activeModels: [],
    primaryUse: "Error tracking; @sentry/replay implies the paid session-replay add-on is enabled",
    production: true,
    confidence: "UNKNOWN",
    notes: "client/sentry.server.config.ts, sentry.edge.config.ts (@sentry/nextjs, @sentry/replay).",
  },
  {
    provider: "Stripe",
    service: "Payments / subscriptions",
    category: "Payments",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "Direct (processing fees, % + per-transaction)",
    activeModels: [],
    primaryUse: "Checkout, billing portal, subscription verification",
    production: true,
    confidence: "UNKNOWN",
    notes: "client/app/api/backend/plan/{createCheckoutSession,createPortalSession,verifyCheckoutSession}/route.ts. Revenue-collection tool, but processing fees are a real recurring cost.",
  },
  {
    provider: "Google Cloud",
    service: "Firebase infra (Hosting / Firestore / Cloud Run)",
    category: "Infra / Hosting",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP invoice",
    activeModels: [],
    primaryUse: "Hosting + Cloud Run deployment across 5 repos",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/firebase.json (3 hosting sites, one rewritten to Cloud Run serviceId 'document'); gmail-server/firebase.json (Cloud Run 'gmail-server-addy'); also deployed by internal-admin-dashboard, sidebar-chat, addy-help-center. Distinct GCP line item from the already-tracked Vertex/Document AI spend.",
  },
  {
    provider: "Google Cloud",
    service: "BigQuery",
    category: "Infra / Hosting",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP invoice",
    activeModels: [],
    primaryUse: "Backing store for internal LLM cost analytics",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/functions/utils/llmCostAnalytics.js — the cost-analytics tooling itself runs through BigQuery, which is its own billed line item.",
  },
  {
    provider: "Google Cloud",
    service: "Cloud Text-to-Speech",
    category: "AI / Voice",
    cost30d: null,
    pctOfMeasured: null,
    billingMethod: "GCP invoice",
    activeModels: [],
    primaryUse: "AI voice route, separate from Gemini's own native TTS",
    production: true,
    confidence: "UNKNOWN",
    notes: "backend/functions/routes/aiVoice.js.",
  },
  {
    provider: "AWS",
    service: "S3 + Textract SDKs",
    category: "Infra / Hosting",
    cost30d: 0,
    pctOfMeasured: 0,
    billingMethod: "n/a",
    activeModels: [],
    primaryUse: "Declared dependency only",
    production: false,
    confidence: "CONFIRMED-inactive",
    notes: "@aws-sdk/client-s3, @aws-sdk/client-textract declared in backend/functions/package.json — zero require/import found anywhere in the codebase.",
  },
  {
    provider: "SerpApi",
    service: "Search API",
    category: "Data",
    cost30d: 0,
    pctOfMeasured: 0,
    billingMethod: "n/a",
    activeModels: [],
    primaryUse: "Declared dependency only — Scale SERP is the SERP provider actually called",
    production: false,
    confidence: "CONFIRMED-inactive",
    notes: "serpapi npm package present only in package.json/package-lock.json, no call sites found.",
  },
];

export const RISKS = [
  {
    id: "R1",
    title: "Vertex Provisioned Throughput spillover",
    detail: "39% of Flash input tokens (9.95B of 25.71B) bypass Provisioned Throughput and bill on-demand — roughly $8.3k/mo.",
  },
  {
    id: "R2",
    title: "gpt-5.4 runaway input tokens",
    detail: "Avg 135,609 input tokens/request. The Aug 10-11 incident alone cost $635 in two days.",
  },
  {
    id: "R3",
    title: "Langfuse understates Gemini cost",
    detail: "langfuseTracing.js:20 maps gemini-3-flash to gemini-2.5-flash pricing, undercounting Gemini 3 Flash spend by ~30-40%.",
  },
  {
    id: "R4",
    title: "Document AI Form Parser 20x overpriced for its volume",
    detail: "$1,536.90 vs $77.58/30d for OCR on nearly the same page count — suggests every document runs both processors unnecessarily.",
  },
  {
    id: "R5",
    title: "Dev environment burning production-grade tokens",
    detail: "addy-ai-dev consumed 1.65B tokens/30d (~$1,306/mo) with no budget cap.",
  },
];

export const OPPORTUNITIES = [
  { id: "O1", priority: "P0", provider: "OpenAI", savings: "$400-700/mo", summary: "Trim gpt-5.4 UW-conditions prompt size, raise cache hit rate, cap spend per job." },
  { id: "O2", priority: "P0", provider: "Google Vertex", savings: "$1,500-3,000/mo", summary: "Right-size Provisioned Throughput or shift batch extraction off-peak / to the Batch API." },
  { id: "O3", priority: "P1", provider: "Google Document AI", savings: "$1,000-1,500/mo", summary: "Gate Form Parser behind a document-type classifier instead of running it on every document." },
  { id: "O4", priority: "P1", provider: "Google Vertex (dev)", savings: "$600-1,000/mo", summary: "Fix the HTTP 403 retry loop and apply a quota cap on the dev project." },
  { id: "O5", priority: "P2", provider: "Anthropic", savings: "$80-110/mo+", summary: "Cascade document classification to a cheaper model, escalating to Opus only on low confidence." },
  { id: "O6", priority: "P2", provider: "OpenAI / Qdrant", savings: "$10-30/mo + storage", summary: "De-duplicate two parallel embedding modules; consider truncated vector dimensions." },
  { id: "O7", priority: "P1 (accuracy)", provider: "Langfuse", savings: "$0 direct", summary: "Fix the Gemini model-alias pricing bug so cost dashboards built on Langfuse stop under-reporting." },
];

export const MISSING_INTEGRATIONS = [
  { provider: "GCP Billing / BigQuery billing export", priority: "REQUIRED", why: "Biggest gap — no direct GCP cost visibility at all, including the Vertex PT commitment." },
  { provider: "Anthropic Usage & Cost API", priority: "REQUIRED", why: "Only Langfuse-traced Anthropic calls are visible; true invoice total is unknown." },
  { provider: "Azure Cost Management", priority: "REQUIRED", why: "Azure OpenAI is a complete blind spot today." },
  { provider: "Vapi API", priority: "HIGH", why: "No visibility into voice-agent cost, and a real double-count risk against the OpenAI gpt-4o line." },
  { provider: "Modal API", priority: "HIGH", why: "Warm-pool GPU OCR can bill for idle time with no current visibility." },
  { provider: "Firestore (read-only)", priority: "HIGH", why: "Needed to attribute AI cost per loan / document / org — currently impossible from telemetry alone." },
  { provider: "GCP Compute Engine inventory + billing", priority: "HIGH", why: "Self-hosted GPU VMs (A100, L4) are real AI cost hidden under Compute, not AI SKUs." },
  { provider: "Extend AI / Cursor / NVIDIA / Groq / Qdrant / LlamaParse / Apify / Scale SERP / Tavily / Algolia", priority: "USEFUL", why: "Smaller individually, but together a meaningful unmeasured tail." },

  // --- Added after the 2026-08-19 non-AI sweep ---
  { provider: "Stripe balance/fees report", priority: "HIGH", why: "Processing-fee cost has never been pulled — Stripe is currently tracked only as a revenue tool, not a cost line." },
  { provider: "Twilio + Telnyx usage/billing API", priority: "HIGH", why: "Usage-based SMS/voice across two redundant providers, zero cost visibility on either." },
  { provider: "HubSpot / Salesforce / GoHighLevel / RadCrm billing", priority: "HIGH", why: "Four separate CRM/marketing platforms with unknown seat + API-tier cost; RadCrm even runs its own monthly partner-billing job we don't reconcile against our own spend." },
  { provider: "Sentry / PostHog / Mixpanel plan cost", priority: "HIGH", why: "Three overlapping analytics/observability subscriptions (plus Langfuse) — likely tiered on event volume, none currently priced out." },
  { provider: "DocuSign / CheckReality per-transaction cost", priority: "MEDIUM", why: "Per-envelope and per-document-check fees that scale with loan volume but aren't in any cost model today." },
  { provider: "SendGrid / Brevo / Calendly / Fireflies / Notion / Zoom / Slack billing", priority: "USEFUL", why: "Smaller recurring SaaS subscriptions found across the workspace that were absent from the original AI-focused audit." },
];
